import React, { useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ShadowPortalProvider } from "@penmate/react-shared/ui/portal";
import { SearchFlow } from "./flow/SearchFlow";
import type { SearchDraft, SearchPrefill } from "./flow/types";

export type SearchNavigateIntent =
  | { to: "login"; query?: SearchDraft }
  | { to: "my-penmates" }
  | { to: "penmate-profile"; penmateId: string }
  | { to: "compose"; penmateId: string };

// Funnel events, forwarded by the host into the metrics pipeline. Event names
// are the wire names — the host strips `type` and sends the rest as payload.
// `search-add-succeeded` is the exception: it exists only so the host can fire
// the GTM ad-conversion; the Amplitude add event is emitted server-side.
export type WaitOutcome =
  | "match_presented"
  | "proceeded_no_match"
  | "proceeded_still_searching"
  | "proceeded_after_decline";

export type SearchEvent =
  | { type: "search-opened" }
  | {
      type: "search-started";
      search_id: number | null;
      state: string;
      has_optional_id: boolean;
      federal: boolean;
    }
  | {
      type: "search-match-presented";
      search_id: number | null;
      source: string;
      // resolved = the attempt carries a mailable facility address id;
      // named = the source merely printed a facility string.
      facility_resolved: boolean;
      facility_named: boolean;
      facility_conflict: boolean;
    }
  | { type: "search-match-confirmed"; search_id: number | null; source: string }
  | { type: "search-match-declined"; search_id: number | null; source: string }
  | {
      type: "search-wait-resolved";
      search_id: number | null;
      waited_ms: number;
      cap_hit: boolean;
      // Why the wait had released by the exit, if it had: the in-progress
      // cap, or a backlogged/offline signal letting go at the minimum wait.
      // Persists onto a later in-place match exit; null when the wait never
      // released (the host drops null values).
      release_reason: "cap" | "backlogged" | "offline" | null;
      outcome: WaitOutcome;
      branch: "known_facility" | "unknown";
      federal: boolean;
      sources_seen: string[];
      search_failed: boolean;
      escaped: boolean;
    }
  | { type: "search-add-succeeded"; penmate_id: number | null }
  // The page was left with a search underway and no add: the funnel's
  // abandonment signal. `wait_resolved` says an exit action was already taken
  // on the wait step (e.g. the escape link) before leaving.
  | {
      type: "search-flow-left";
      step: "who" | "where" | "wait";
      search_id: number | null;
      wait_resolved: boolean;
      add_pending: boolean;
    };

export type PmReactSearchProps = {
  isAuthenticated?: boolean;
  user?: unknown;
  sessionId?: number;
  resumeQuery?: SearchDraft;
  // Inbound pre-fill from the URL — seeds the first screen, never fires.
  prefill?: SearchPrefill;
  // Session-attribution context the server folds into its add-penmate event —
  // read at add time, so the POST sees the live session (parity with the
  // legacy add path).
  getMetricsContext?: () => Record<string, unknown> | undefined;
  onNavigate?: (intent: SearchNavigateIntent) => void;
  onEvent?: (event: SearchEvent) => void;
  // Wired by the bundle entry: the flow registers what to do when the host
  // reports its page is being left (`PmReact.search.leave(el)`).
  registerLeave?: (handler: (() => void) | null) => void;
};

export const PmReactSearchApp = (props: PmReactSearchProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: 1, refetchOnWindowFocus: false },
        },
      }),
  );

  return (
    <div className="pm-react-root">
      <QueryClientProvider client={queryClient}>
        <ShadowPortalProvider>
          <SearchFlow {...props} />
        </ShadowPortalProvider>
      </QueryClientProvider>
    </div>
  );
};
